import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Sparkles, Check, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * LocationAutocomplete - Google Places Autocomplete (NEW API - 2025+)
 * 
 * MODERNIZED: Uses AutocompleteSuggestion + Place.fetchFields (Places API New)
 * NO LEGACY CODE - Fully compatible with Google's 2025+ requirements
 * 
 * @param {string|object} value - Display value or location object
 * @param {function} onChange - Called with: { display, lat, lng, utc_offset_minutes, place_id }
 * @param {string} placeholder - Input placeholder text
 */
export const LocationAutocomplete = ({ value, onChange, placeholder = "City, Country..." }) => {
    const [inputValue, setInputValue] = useState(typeof value === 'string' ? value : value?.display || "");
    const [suggestions, setSuggestions] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [showAccepted, setShowAccepted] = useState(false);
    const [apiReady, setApiReady] = useState(false);
    const [apiError, setApiError] = useState(null);
    
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);
    const searchTimeoutRef = useRef(null);
    const sessionTokenRef = useRef(null);

    // Initialize NEW Google Places API
    useEffect(() => {
        const initNewPlacesAPI = async () => {
            try {
                // Wait for google.maps to be available
                if (!window.google?.maps) {
                    return false;
                }
                
                // Import the places library dynamically (NEW API requirement)
                const { AutocompleteSessionToken, AutocompleteSuggestion } = 
                    await window.google.maps.importLibrary("places");
                
                // Create session token for billing optimization
                sessionTokenRef.current = new AutocompleteSessionToken();
                
                setApiReady(true);
                setApiError(null);
                console.log("✨ Google Places API (NEW) initialized successfully");
                return true;
                
            } catch (e) {
                console.error("Places API (NEW) init error:", e);
                setApiError(e.message);
                return false;
            }
        };

        // Try to initialize immediately and with retries
        const attemptInit = async () => {
            if (await initNewPlacesAPI()) return;
            
            // Retry with delays
            const delays = [500, 1000, 2000, 3000, 5000];
            for (const delay of delays) {
                await new Promise(r => setTimeout(r, delay));
                if (await initNewPlacesAPI()) return;
            }
        };
        
        attemptInit();
    }, []);

    useEffect(() => {
        const displayValue = typeof value === 'string' ? value : value?.display || "";
        // Always update input when value changes (especially after getPlaceDetails updates it)
        setInputValue(displayValue);
    }, [value?.display, value]);

    // Parse place description into structured parts
    const parsePlaceDescription = (text) => {
        if (!text) return { city: '', region: '', country: '' };
        const parts = text.split(', ');
        if (parts.length >= 2) {
            const city = parts[0];
            const country = parts[parts.length - 1];
            const region = parts.length > 2 ? parts.slice(1, -1).join(', ') : '';
            return { city, region, country };
        }
        return { city: text, region: '', country: '' };
    };

    /**
     * Fetch autocomplete suggestions using NEW API (AutocompleteSuggestion)
     */
    const fetchSuggestions = useCallback(async (input) => {
        if (!apiReady || !input || input.length < 2) {
            setSuggestions([]);
            setIsOpen(false);
            return;
        }

        setIsSearching(true);
        
        try {
            const { AutocompleteSuggestion } = await window.google.maps.importLibrary("places");
            
            // Use the NEW AutocompleteSuggestion.fetchAutocompleteSuggestions
            const request = {
                input: input,
                includedPrimaryTypes: ['(cities)'], // Filter to cities only
                sessionToken: sessionTokenRef.current,
                language: navigator.language?.split('-')[0] || 'en'
            };
            
            const response = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
            
            if (response?.suggestions?.length > 0) {
                const formattedSuggestions = response.suggestions.slice(0, 5).map(suggestion => ({
                    placeId: suggestion.placePrediction.placeId,
                    mainText: suggestion.placePrediction.mainText?.text || '',
                    secondaryText: suggestion.placePrediction.secondaryText?.text || '',
                    fullText: suggestion.placePrediction.text?.text || ''
                }));
                
                setSuggestions(formattedSuggestions);
                setIsOpen(true);
            } else {
                setSuggestions([]);
                setIsOpen(false);
            }
            
        } catch (e) {
            console.error("Autocomplete fetch error:", e);
            setSuggestions([]);
            setIsOpen(false);
        } finally {
            setIsSearching(false);
        }
    }, [apiReady]);

    /**
     * Get full place details using NEW API (Place.fetchFields)
     */
    const getPlaceDetails = useCallback(async (placeId, displayName) => {
        try {
            const { Place } = await window.google.maps.importLibrary("places");
            
            // Create Place instance and fetch details including address components
            const place = new Place({ id: placeId });
            
            await place.fetchFields({
                fields: ['location', 'utcOffsetMinutes', 'formattedAddress', 'displayName', 'addressComponents']
            });
            
            const lat = place.location?.lat();
            const lng = place.location?.lng();
            const utcOffset = place.utcOffsetMinutes;
            
            // Parse address components for "City, State, Country" format
            let city = '';
            let state = '';
            let country = '';
            
            if (place.addressComponents) {
                for (const component of place.addressComponents) {
                    const types = component.types || [];
                    if (types.includes('locality')) {
                        city = component.longText || component.shortText;
                    } else if (types.includes('administrative_area_level_1')) {
                        state = component.longText || component.shortText;
                    } else if (types.includes('country')) {
                        country = component.longText || component.shortText;
                    }
                }
            }
            
            // Build formatted location string: "City, State, Country"
            const locationParts = [city, state, country].filter(Boolean);
            const formattedLocation = locationParts.length > 0 ? locationParts.join(', ') : displayName;
            
            console.log(`📍 Location captured (NEW API): ${formattedLocation}`);
            console.log(`   Coordinates: lat=${lat}, lng=${lng}`);
            console.log(`   UTC Offset: ${utcOffset !== undefined ? utcOffset + ' minutes' : 'not available'}`);
            console.log(`   Components: city=${city}, state=${state}, country=${country}`);
            
            // Create new session token for next search
            const { AutocompleteSessionToken } = await window.google.maps.importLibrary("places");
            sessionTokenRef.current = new AutocompleteSessionToken();
            
            onChange({
                display: formattedLocation,
                lat: lat,
                lng: lng,
                utc_offset_minutes: utcOffset ?? null,
                place_id: placeId,
                city: city,
                state: state,
                country: country
            });
            
        } catch (e) {
            console.error("Place details fetch error:", e);
            // Return display name only on error
            onChange({ 
                display: displayName, 
                lat: null, 
                lng: null, 
                utc_offset_minutes: null, 
                place_id: placeId 
            });
        }
    }, [onChange]);

    const handleInputChange = (e) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        setHighlightedIndex(-1);
        setShowAccepted(false);
        
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

        if (newValue.length >= 2) {
            searchTimeoutRef.current = setTimeout(() => {
                fetchSuggestions(newValue);
            }, 300);
        } else {
            setSuggestions([]);
            setIsOpen(false);
        }
    };

    const handleSelectSuggestion = async (suggestion) => {
        // Temporarily set display text while fetching details
        const tempDisplayText = suggestion.mainText + (suggestion.secondaryText ? `, ${suggestion.secondaryText}` : '');
        setInputValue(tempDisplayText);
        setIsOpen(false);
        setSuggestions([]);
        
        // Fetch full place details - this will update with proper "City, State, Country" format
        await getPlaceDetails(suggestion.placeId, tempDisplayText);
        
        setShowAccepted(true);
        setTimeout(() => setShowAccepted(false), 1500);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
                handleSelectSuggestion(suggestions[highlightedIndex]);
            } else if (inputValue) {
                // Manual entry without selection
                onChange({ display: inputValue, lat: null, lng: null, utc_offset_minutes: null, place_id: null });
                setShowAccepted(true);
                setTimeout(() => setShowAccepted(false), 1500);
                setIsOpen(false);
            }
            return;
        }

        if (!isOpen || !suggestions.length) return;

        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlightedIndex(prev => Math.min(prev + 1, suggestions.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlightedIndex(prev => Math.max(prev - 1, 0));
        } else if (e.key === "Escape") {
            setIsOpen(false);
        }
    };

    const handleBlur = () => {
        setTimeout(() => {
            setIsOpen(false);
            if (inputValue && inputValue !== (typeof value === 'string' ? value : value?.display)) {
                onChange({ display: inputValue, lat: null, lng: null, utc_offset_minutes: null, place_id: null });
            }
        }, 200);
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
                inputRef.current && !inputRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        };
    }, []);

    return (
        <div className="relative w-full">
            <div className="relative">
                <MapPin className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-primary/50 pointer-events-none" />
                <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onFocus={() => suggestions.length > 0 && setIsOpen(true)}
                    onBlur={handleBlur}
                    placeholder={placeholder}
                    autoComplete="off"
                    data-testid="location-autocomplete-input"
                    className={cn(
                        "w-full bg-transparent",
                        "border-0 border-b border-primary/50",
                        "pl-6 pr-0 py-3",
                        "text-foreground text-base text-center",
                        "placeholder:text-muted-foreground placeholder:italic",
                        "font-inter tracking-wide",
                        "focus:outline-none focus:border-primary",
                        "transition-all duration-300"
                    )}
                />
            </div>

            {/* Status Indicators */}
            <AnimatePresence mode="wait">
                {isSearching && (
                    <motion.div
                        key="searching"
                        className="absolute left-0 right-0 mt-2 flex items-center justify-center gap-2"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}>
                            <Sparkles className="w-3 h-3 text-primary/60" />
                        </motion.div>
                        <span className="text-primary/60 text-xs font-inter italic">consulting the stars...</span>
                    </motion.div>
                )}
                
                {showAccepted && !isSearching && (
                    <motion.div
                        key="accepted"
                        className="absolute left-0 right-0 mt-2 flex items-center justify-center gap-2"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <Check className="w-3 h-3 text-primary" />
                        <span className="text-primary text-xs font-inter">location accepted</span>
                    </motion.div>
                )}

                {apiError && !isSearching && !showAccepted && (
                    <motion.div
                        key="error"
                        className="absolute left-0 right-0 mt-2 flex items-center justify-center gap-2"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <AlertCircle className="w-3 h-3 text-red-400/60" />
                        <span className="text-red-400/60 text-xs font-inter">type your city manually</span>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Dark Luxury Dropdown */}
            <AnimatePresence>
                {isOpen && suggestions.length > 0 && (
                    <motion.div
                        ref={dropdownRef}
                        className="absolute z-[9999] w-full mt-3 rounded overflow-hidden"
                        style={{
                            background: "#0A0A0B",
                            border: "1px solid #D4AF37",
                            boxShadow: "0 12px 48px rgba(0,0,0,0.7), 0 0 24px rgba(212,175,55,0.08)"
                        }}
                        initial={{ opacity: 0, y: -10, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.98 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        data-testid="location-dropdown"
                    >
                        {suggestions.map((s, i) => {
                            const { city, region, country } = parsePlaceDescription(s.secondaryText);
                            const mainCity = s.mainText;
                            const secondLine = s.secondaryText || '';
                            
                            return (
                                <button
                                    key={s.placeId}
                                    type="button"
                                    data-testid={`location-option-${i}`}
                                    className={cn(
                                        "w-full px-4 py-3 text-left flex flex-col gap-0.5 transition-colors duration-150",
                                        i !== 0 && "border-t border-[rgba(212,175,55,0.15)]",
                                        highlightedIndex === i 
                                            ? "bg-[rgba(212,175,55,0.12)]" 
                                            : "hover:bg-[rgba(212,175,55,0.08)]"
                                    )}
                                    onMouseDown={(e) => { e.preventDefault(); handleSelectSuggestion(s); }}
                                    onMouseEnter={() => setHighlightedIndex(i)}
                                >
                                    {/* Top Line: City Name - White, 16px */}
                                    <span 
                                        className="font-inter font-medium tracking-wide"
                                        style={{ color: "#FFFFFF", fontSize: "16px" }}
                                    >
                                        {mainCity}
                                    </span>
                                    {/* Bottom Line: Region & Country - Dimmed Gold, 12px */}
                                    {secondLine && (
                                        <span 
                                            className="font-inter"
                                            style={{ color: "rgba(212, 175, 55, 0.6)", fontSize: "12px" }}
                                        >
                                            {secondLine}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                        {/* Powered by Google footer */}
                        <div 
                            className="px-4 py-2 text-right"
                            style={{ borderTop: "1px solid rgba(212,175,55,0.15)" }}
                        >
                            <span 
                                className="font-inter"
                                style={{ color: "rgba(245,245,245,0.25)", fontSize: "9px" }}
                            >
                                powered by Google
                            </span>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default LocationAutocomplete;
